import type { Chart, ChartData, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import commaNumber from 'comma-number';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { CenterFlex } from '@/components/base';
import {
  CHART_GRAPH_DIRECTION,
  CHART_METRIC_VIEW_TYPE,
} from '@/entities/chart/model/constants';
import useEventPlugin from '@/features/(charts)/chart-renderer/hooks/use-chart-event-plugin';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import {
  BaseBarChart,
  useBarChartShowMore,
  useChartTooltip,
} from '@/shared/ui/charts';
import { getContrastTextColor } from '@/styles/colors/color.utils';

import ChartCustomTooltip from '../chart-custom-tooltip/ChartCustomTooltip';
import useChartCrossAxisData from './useChartCrossAxisData';

// 비-대시보드 수평 bar chart 스크롤 관련 상수
const DESIRED_BAR_HEIGHT = 28;
const MAX_CHART_HEIGHT = 15000;
const MAX_LABEL_LENGTH = 35;

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartCrossTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
};

const CrossStackedBarChart = (props: Props) => {
  const color = useColor();
  const chartRef = useRef<Chart>(null);

  const barChartData = useChartCrossAxisData(props);

  const { tooltipData, chartTooltip, hideTooltip } = useChartTooltip({
    chart: chartRef.current,
  });

  const eventPlugin = useEventPlugin({
    meta: props.chart,
    hideTooltip,
    dashboardCardInfo: props.dashboardCardInfo,
  });

  const isPercent =
    props.chart.series[0]?.options?.view_type ===
    CHART_METRIC_VIEW_TYPE.PERCENTAGE;

  const isVertical =
    props.chart.graph_direction === CHART_GRAPH_DIRECTION.VERTICAL;
  const isDashboard = !!props.dashboardCardInfo;
  const labelCount = barChartData.labels?.length ?? 0;

  // 대시보드 수평 bar chart show more 로직
  const {
    containerRef,
    expanded,
    setExpanded,
    hasMore,
    visibleCount,
    totalCount,
    expandedChartHeight,
    collapsedChartHeight,
    isHorizontal,
    chartAreaLeft,
  } = useBarChartShowMore({
    labels: barChartData.labels as string[] | undefined,
    isVertical,
    isDashboard,
    chartRef,
  });

  // 비-대시보드: 스크롤 높이 측정
  const [scrollHeight, setScrollHeight] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    // 대시보드 수평 bar chart: show more/less가 scroll을 CSS로 직접 관리
    if (isDashboard && isHorizontal) return;

    if (isVertical || labelCount === 0) {
      setScrollHeight((prev) => (prev === undefined ? prev : undefined));
      return;
    }

    let retryCount = 0;
    let rafId = 0;

    const measure = () => {
      const chart = chartRef.current;
      const yScale = chart?.scales?.['y'];

      if (!chart || !yScale || !yScale.height) {
        if (retryCount < 10) {
          retryCount += 1;
          rafId = requestAnimationFrame(measure);
        }
        return;
      }

      const totalItems = labelCount;

      const perBarHeight = yScale.height / totalItems;
      const chartOverhead = Math.max(chart.height - yScale.height, 0);

      let nextHeight: number | undefined;
      if (perBarHeight < DESIRED_BAR_HEIGHT) {
        const scrollNeeded = Math.ceil(
          totalItems * DESIRED_BAR_HEIGHT + chartOverhead,
        );
        nextHeight = Math.min(scrollNeeded, MAX_CHART_HEIGHT);
      }

      setScrollHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    };

    rafId = requestAnimationFrame(measure);

    return () => cancelAnimationFrame(rafId);
     
  }, [isVertical, labelCount, isDashboard, isHorizontal]);

  // 데이터 슬라이싱: collapsed 모드에서 표시할 데이터만 필터링
  const visibleBarChartData = useMemo(() => {
    if (!hasMore || expanded) return barChartData;
    return {
      ...barChartData,
      labels: barChartData.labels?.slice(0, visibleCount),
      datasets: barChartData.datasets.map((ds) => ({
        ...ds,
        data: ds.data.slice(0, visibleCount),
        backgroundColor: Array.isArray(ds.backgroundColor)
          ? ds.backgroundColor.slice(0, visibleCount)
          : ds.backgroundColor,
      })),
    };
  }, [barChartData, hasMore, expanded, visibleCount]);

  // y축 label 콜백용 visible labels
  const visibleLabels = useMemo(() => {
    const allLabels = barChartData.labels as string[] | undefined;
    if (!hasMore || expanded) return allLabels;
    return allLabels?.slice(0, visibleCount);
  }, [barChartData.labels, hasMore, expanded, visibleCount]);

  const barChartOptions: ChartOptions<'bar'> = useMemo(() => {
    return {
      indexAxis: isVertical ? 'x' : 'y',

      scales: {
        [isVertical ? 'y' : 'x']: {
          grid: {
            color: 'transparent',
          },
          min: props.chart.series[0]?.options?.scale_min || 0,
          max: props.chart.series[0]?.options?.scale_max,
          stacked: true,
          ticks: {
            callback(value) {
              if (isPercent) return `${value}%`;
              return value;
            },
          },
        },
        [isVertical ? 'x' : 'y']: {
          stacked: true,
          title: {
            display: false,
          },
          ticks: {
            callback(value: string | number, index: number) {
              const labelIndex = Number(value);
              const label =
                visibleLabels?.[labelIndex] ??
                visibleLabels?.[index] ??
                String(value);

              if (typeof label !== 'string') return String(value);
              if (isVertical) return label;

              return label.length > MAX_LABEL_LENGTH
                ? label.slice(0, MAX_LABEL_LENGTH) + '…'
                : label;
            },
          },
        },
      },
      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'mousemove', 'click'],
        },
        legend: {
          labels: {
            color: color.gray700,
          },
          position: 'right',
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
        datalabels: {
          align: 'center',
          anchor: 'center',
          display: 'auto',
          color: (context) => {
            const backgroundColor = context.dataset.backgroundColor;
            const segmentColor = Array.isArray(backgroundColor)
              ? backgroundColor[context.dataIndex]
              : backgroundColor;

            return getContrastTextColor(
              typeof segmentColor === 'string' ? segmentColor : '#FFFFFF',
            );
          },
          formatter: (value) => {
            if (Number(value) === 0) return null;

            const formattedValue = commaNumber(Number(value));
            return isPercent ? `${formattedValue}%` : formattedValue;
          },
        },
      },
    };
  }, [
    visibleLabels,
    chartTooltip,
    color.gray700,
    isPercent,
    isVertical,
    props.chart.series,
  ]);

  const chartContent = (
    <CenterFlex className="relative w-full flex-1 h-full">
      <BaseBarChart
        chartRef={chartRef as React.RefObject<Chart<'bar'>>}
        data={visibleBarChartData as ChartData<'bar'>}
        options={barChartOptions}
        plugins={[eventPlugin, ChartDataLabels]}
      />
      {tooltipData && (
        <ChartCustomTooltip
          tooltip={tooltipData}
          meta={props.chart}
          tableData={props.tableData}
          dashboardCardInfo={props.dashboardCardInfo}
        />
      )}
    </CenterFlex>
  );

  // chart 영역 높이: expanded면 전체 bar, collapsed면 visible bars만
  const chartAreaHeight = expandedChartHeight ?? collapsedChartHeight;

  // Dashboard 수평 bar chart: show more/less 지원
  if (isDashboard && isHorizontal) {
    return (
      <div ref={containerRef} className="flex flex-col w-full flex-1 min-h-0">
        <div
          className={`w-full overflow-x-hidden h-full ${expanded ? 'flex-1 min-h-0' : ''}`}
          style={{ overflowY: expandedChartHeight ? 'auto' : 'hidden' }}
        >
          <div
            className="relative w-full"
            style={{ height: chartAreaHeight ?? '100%' }}
          >
            {chartContent}
          </div>
        </div>
        {hasMore && (
          <div
            className="flex justify-start pt-1"
            style={{ paddingLeft: chartAreaLeft }}
          >
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="text-xs text-gray-500 hover:text-gray-800 hover:underline cursor-pointer bg-transparent border-0 py-0.5"
            >
              {expanded ? 'Collapse' : `Show all ${totalCount} items`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // 비-대시보드: 기존 scroll wrapper 동작 유지
  if (scrollHeight) {
    return (
      <div className="flex-1 w-full overflow-y-auto overflow-x-hidden">
        <div
          style={{ height: '100%', minHeight: scrollHeight }}
          className="relative w-full"
        >
          {chartContent}
        </div>
      </div>
    );
  }

  return chartContent;
};

export default CrossStackedBarChart;
